import { formatCount } from './coverage';
import { parseLocationId } from './locationId';

export interface ParsedLocationIds {
  // Deduped, in first-appearance order. Order is not the contract's business — the upsert is
  // per-id — but a stable order is what makes the parse testable and its output readable.
  ids: number[];
  // How many NON-EMPTY tokens failed the integer test. Reported as a count and never as a list:
  // on a paste of thousands, an enumeration of rejects is longer than the screen and buries its
  // own headline.
  discarded: number;
}

// A copied spreadsheet column arrives newline-separated, a copied row tab- or comma-separated, and
// a hand-typed list however the agent types it. All three are the same intent.
const SEPARATORS = /[\s,;]+/;

// What the panel will actually write, decided here rather than by the endpoint: the contract 400s
// the whole batch over a single bad token, so this is the only filter between a spreadsheet paste
// and thousands of buildings.
//
// The per-token rule belongs to `parseLocationId`; what this adds around it is the splitting, the
// dedupe and the reject count. That the rule cannot tell an id that EXISTS from one that merely
// looks like one is why the readout below says «formato válido».
export function parseLocationIds(input: string): ParsedLocationIds {
  const seen = new Set<number>();
  const ids: number[] = [];
  let discarded = 0;

  for (const token of input.split(SEPARATORS)) {
    // Dropped silently, not counted: essentially every paste out of Sheets carries a trailing
    // newline, and an empty string is not a token anybody typed.
    if (token === '') continue;

    const id = parseLocationId(token);
    if (id === null) {
      discarded += 1;
      continue;
    }

    // A repeat is one write either way — the endpoint is an idempotent upsert — so it collapses
    // rather than raising an alarm about nothing.
    if (seen.has(id)) continue;
    seen.add(id);
    ids.push(id);
  }

  return { ids, discarded };
}

// The one line the agent reads carefully before an irreversible write, so both halves agree in
// number. «con FORMATO válido», never «válidos»: the panel cannot verify that an id exists.
//
// Counts ride `formatCount`, the same grouped es-ES formatter the counter card uses — one screen
// cannot spell the same kind of quantity two ways.
//
// TODO: i18n — copy en ES hasta Fase 1.
export function formatParseSummary({ ids, discarded }: ParsedLocationIds): string {
  // An untouched field has nothing to report, and this line shares the helper slot with the input
  // rule: a «0 IDs» over an empty box is noise where that rule should be.
  if (ids.length === 0 && discarded === 0) return '';

  const valid = `${formatCount(ids.length)} ${ids.length === 1 ? 'ID' : 'IDs'} con formato válido`;
  if (discarded === 0) return valid;

  const rejects = `${formatCount(discarded)} ${discarded === 1 ? 'descartado' : 'descartados'}`;
  return `${valid} · ${rejects}`;
}
